/**
 * Reloading, which is the one browser habit that does not survive a native
 * shell.
 *
 * Not one of the nine adapters `MOBILE_SUPPLEMENT.md` 2.1 lists. Added because
 * `packages/ui`'s ErrorBoundary called `window.location.reload()` directly, and
 * that is a genuine platform difference rather than an incidental use of a
 * global: a webview under Capacitor has no URL bar, no reload button, and
 * reloading throws away an in-memory event queue that has not been flushed.
 *
 * ADR-0021 records the scope decision this sits inside: adapt what genuinely
 * DIFFERS between hosts rather than every reference to `window`. `matchMedia`
 * and `document` behave identically in a webview and stay where they are.
 */

import { browserWindow, type Adapter } from './host.js';

export interface Navigation extends Adapter {
  /**
   * Start the application over.
   *
   * On the web this is a page reload. Under Capacitor it will re-enter the
   * root route without discarding the process, which is why callers must not
   * assume the URL changes or that module state is cleared.
   */
  readonly restart: () => void;

  /**
   * Load the app's start route as a full load, leaving the current URL behind.
   *
   * For a crash that repeats on every render of one URL, where `restart` would
   * just reload the same broken screen. The caller supplies the start path
   * (the web app passes its Vite base, which is the manifest `start_url`)
   * because this package does not know where the app is mounted. Under
   * Capacitor this will re-enter the root route.
   */
  readonly goToStart: (startPath: string) => void;
}

export const navigation: Navigation = {
  isSupported: () => browserWindow() !== null,

  restart: () => {
    browserWindow()?.location.reload();
  },

  goToStart: (startPath) => {
    browserWindow()?.location.assign(startPath);
  },
};
